/**
 * FEMA Foreign-Exchange Realisation dashboard.
 *
 * RBI Master Direction · export payment must land in India within 9 months.
 * Buckets: 0-90 (green) · 91-180 (amber) · 181-270 (red) · 270+ (CRITICAL breach)
 * Data source: GET /api/reports/fema-realisation?as_of=YYYY-MM-DD
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, Ship } from "lucide-react";

const fmtINR = (v) => "₹ " + Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtFX = (v, c) => `${c || ""} ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SEVERITY = {
  green:    ["text-emerald-600 border-emerald-500/40 bg-emerald-500/5", "0-90 d"],
  amber:    ["text-amber-600 border-amber-500/40 bg-amber-500/5", "91-180 d"],
  red:      ["text-red-500 border-red-500/40 bg-red-500/5", "181-270 d · > 6 mo"],
  critical: ["text-red-700 border-red-700 bg-red-700/10 font-bold", "270+ d · FEMA BREACH"],
};

export default function FemaRealisation() {
  const today = new Date().toISOString().slice(0,10);
  const [asOf, setAsOf] = useState(today);
  const [includePaid, setIncludePaid] = useState(false);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = () => {
    setLoading(true);
    api.get("/reports/fema-realisation", { params: { as_of: asOf, include_paid: includePaid } })
      .then(r => setD(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [asOf, includePaid]);

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="fema-realisation-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// FEMA · 9-month realisation</div>
          <h1 className="text-3xl font-bold mt-1 font-heading">Foreign Exchange Realisation</h1>
          <div className="text-sm text-muted-foreground mt-1 leading-relaxed">
            Every unrealised export/SEZ invoice by ageing bucket. RBI mandates
            realisation within 9 months (270 days) — invoices beyond that are
            FEMA breaches requiring extension applications.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">As of</Label>
          <Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)}
            className="h-8 w-40 text-sm" data-testid="fema-as-of" />
          <label className="flex items-center gap-1.5 text-xs cursor-pointer ml-2">
            <Switch checked={includePaid} onCheckedChange={setIncludePaid} data-testid="fema-include-paid" />
            Include realised
          </label>
        </div>
      </div>

      {/* Summary cards */}
      {d && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="fema-summary">
          {["green","amber","red","critical"].map(sev => {
            const [cls, label] = SEVERITY[sev];
            return (
              <div key={sev} className={`card-sharp p-4 border-l-4 ${cls}`} data-testid={`fema-summary-${sev}`}>
                <div className="text-[10px] font-mono uppercase tracking-widest">{label}</div>
                <div className="text-2xl font-bold mt-1 font-mono tabular-nums">{fmtINR(d.totals_by_severity[sev] || 0)}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">outstanding INR</div>
              </div>
            );
          })}
        </div>
      )}

      {d?.fema_breach_count > 0 && (
        <div className="card-sharp p-3 border-l-4 border-l-red-700 bg-red-700/5 flex items-start gap-2" data-testid="fema-breach-alert">
          <ShieldAlert className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">
            <b>{d.fema_breach_count} invoice(s) breach the FEMA 9-month rule.</b> File
            an extension application with your AD Category-I bank (form ETX) or realise
            the payment ASAP. Continued default may trigger contravention proceedings
            under FEMA Section 13.
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card-sharp overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-2.5">Invoice</th>
              <th className="text-left p-2.5">Customer</th>
              <th className="text-left p-2.5">Type</th>
              <th className="text-right p-2.5">Foreign Amt</th>
              <th className="text-right p-2.5">INR Balance</th>
              <th className="text-center p-2.5">Days Out</th>
              <th className="text-center p-2.5">Bucket</th>
              <th className="text-left p-2.5">Rule</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={8} className="text-center text-muted-foreground p-6">Loading…</td></tr>}
            {!loading && (d?.rows || []).length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted-foreground p-6 italic">No export/SEZ balances open · all realised on time</td></tr>
            )}
            {(d?.rows || []).map((r, i) => {
              const [cls] = SEVERITY[r.severity];
              return (
                <tr key={r.invoice_id} className="hover:bg-muted/30" data-testid={`fema-row-${i}`}>
                  <td className="p-2.5">
                    <div className="font-mono font-semibold">{r.invoice_number}</div>
                    <div className="text-[10px] text-muted-foreground">{r.invoice_date}{r.shipping_bill_no ? ` · SB ${r.shipping_bill_no}` : ""}</div>
                  </td>
                  <td className="p-2.5">
                    <div className="font-medium">{r.customer_name}</div>
                    {r.customer_country && <div className="text-[10px] text-muted-foreground">{r.customer_country}</div>}
                  </td>
                  <td className="p-2.5">
                    <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 border bg-muted/30">
                      {r.is_sez ? "SEZ" : (r.is_service_export ? "SVC EXPORT" : "GOODS EXPORT")}
                    </span>
                  </td>
                  <td className="p-2.5 text-right font-mono tabular-nums">
                    {fmtFX(r.balance_fx, r.currency)}
                    <div className="text-[9px] text-muted-foreground">@ {Number(r.fx_rate).toFixed(4)}</div>
                  </td>
                  <td className="p-2.5 text-right font-mono tabular-nums font-semibold">{fmtINR(r.balance_inr)}</td>
                  <td className="p-2.5 text-center font-mono">
                    <span className={`inline-block px-2 py-0.5 border ${cls}`}>{r.days_outstanding}</span>
                    {!r.is_breached && <div className="text-[9px] text-muted-foreground mt-0.5">{r.days_to_breach}d left</div>}
                  </td>
                  <td className="p-2.5 text-center text-[10px] font-mono uppercase">{r.bucket}</td>
                  <td className="p-2.5 text-[10px] text-muted-foreground">{r.rule}</td>
                </tr>
              );
            })}
          </tbody>
          {d?.grand_total_inr > 0 && (
            <tfoot className="bg-muted/40 font-semibold">
              <tr>
                <td colSpan={4} className="p-2.5 text-right">Total unrealised</td>
                <td className="p-2.5 text-right font-mono">{fmtINR(d.grand_total_inr)}</td>
                <td colSpan={3} className="p-2.5 text-left text-[10px] text-muted-foreground font-normal">
                  {Object.entries(d.grand_total_fx || {}).map(([c,v]) => `${fmtFX(v, c)}`).join(" · ")}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
